import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  // Check token expiration
  const isTokenExpired = (token) => {
    if (!token) return true;
    
    try {
      const payload = JSON.parse(atob(token.split('.')[1]));
      const currentTime = Date.now() / 1000;
      return payload.exp < currentTime;
    } catch (error) {
      return true;
    }
  };

  // Logout function (memoized)
  const logout = useCallback(() => {
    localStorage.removeItem('authToken');
    localStorage.removeItem('refreshToken');
    localStorage.removeItem('userEmail');
    localStorage.removeItem('userId');
    setIsAuthenticated(false);
    setUser(null);
    // Note: Navigation will be handled by components using useNavigate
  }, []);

  // Refresh token function
  const refreshToken = useCallback(async () => {
    const refreshTokenValue = localStorage.getItem('refreshToken');
    if (!refreshTokenValue) {
      // ensure logout is called below (logout is memoized)
      logout();
      return false;
    }

    try {
      const response = await fetch('https://pulse.upstartaec.com/agent/auth/refresh', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ refresh_token: refreshTokenValue }),
      });

      if (response.ok) {
        const data = await response.json();
        localStorage.setItem('authToken', data.data.access_token);
        localStorage.setItem('refreshToken', data.data.refresh_token);
        return true;
      } else {
        logout();
        return false;
      }
    } catch (error) {
      logout();
      return false;
    }
  }, [logout]);


  // Set up token expiration check and initial authentication validation
  useEffect(() => {
    (async () => {
      const token = localStorage.getItem('authToken');
      const userEmail = localStorage.getItem('userEmail');
      const userId = localStorage.getItem('userId');

      if (!token || !userEmail || !userId) {
        setIsAuthenticated(false);
        setUser(null);
        setLoading(false);
      } else {
        if (isTokenExpired(token)) {
          const refreshed = await refreshToken();
          if (!refreshed) {
            setLoading(false);
            return;
          }
        }

        setIsAuthenticated(true);
        setUser({ email: userEmail, id: userId });
        setLoading(false);
      }
    })();

    // Check token expiration every minute
    const interval = setInterval(() => {
      const token = localStorage.getItem('authToken');
      if (token && isTokenExpired(token)) {
        logout();
      }
    }, 60000);

    return () => clearInterval(interval);
  }, [refreshToken, logout]);

  // Login function
  const login = async (email, password) => {
    try {
      const response = await fetch('https://pulse.upstartaec.com/agent/auth/login', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ email, password }),
      });

      const data = await response.json();

      if (data.status === 'success') {
        localStorage.setItem('authToken', data.data.access_token);
        localStorage.setItem('refreshToken', data.data.refresh_token);
        localStorage.setItem('userEmail', email);
        localStorage.setItem('userId', data.data.user_id);
        
        setIsAuthenticated(true);
        setUser({ email, id: data.data.user_id });
        return { success: true };
      } else {
        return { success: false, message: data.message || 'Login failed' };
      }
    } catch (error) {
      return { success: false, message: 'Network error. Please try again.' };
    }
  };

  const value = {
    isAuthenticated,
    user,
    loading,
    login,
    logout,
    refreshToken,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};
